/**
 * The app's single auth entry point, dispatching across identity providers.
 *
 * Two providers exist:
 * - `msal` — Microsoft Entra, for every client with an Entra tenant plus
 *   iQuest's own staff. Two MSAL instances live behind it (see msal.ts).
 * - `google` — Google Workspace, for clients with no Entra tenant at all.
 *
 * Everything outside this directory imports from HERE and never from a
 * provider module, so no page, service or component knows which provider is
 * active. `api.ts` in particular just calls `getAccessToken()`.
 *
 * The active provider is decided ONCE, during `initAuth()`, and then fixed for
 * the life of the page. Re-deriving it per call would let a stale MSAL cache
 * and a live Google session disagree about who is signed in.
 */

import * as google from "./google";
import * as msal from "./msal";

export type Provider = "msal" | "google";

export type AuthAccount = {
  provider: Provider;
  /** Best-effort display name. Present for both providers. */
  name: string;
  /** Best-effort email/username. Present for both providers. */
  email: string;
};

let active: Provider | null = null;

/** Whether the Google sign-in button should be offered at all. */
export const googleEnabled =
  String(import.meta.env.VITE_FEATURE_GOOGLE_SSO ?? "")
    .trim()
    .toLowerCase() === "true";

/**
 * Resolve the signed-in identity, if any.
 *
 * Order matters. The Google callback is checked FIRST, because that navigation
 * carries the session in its fragment and is the only chance to read it: MSAL's
 * own `handleRedirectPromise` runs during its init and would otherwise process
 * the same URL first.
 */
export async function initAuth(): Promise<AuthAccount | null> {
  if (googleEnabled) {
    if (google.isCallbackUrl()) {
      if (google.consumeCallback()) {
        active = "google";
        return currentAccount();
      }
      // A failed Google sign-in (declined, unknown user, expired state). The
      // reason is stashed for the sign-in page; fall through so an existing
      // Entra session is still honoured.
    } else if (await google.restore()) {
      active = "google";
      return currentAccount();
    }
  }

  const account = await msal.initAuth();
  if (account) {
    active = "msal";
    return currentAccount();
  }
  return null;
}

export function currentAccount(): AuthAccount | null {
  if (active === "google") {
    const a = google.currentAccount();
    return a && { provider: "google", name: a.name, email: a.email };
  }
  if (active === "msal") {
    const a = msal.currentAccount();
    return (
      a && {
        provider: "msal",
        name: a.name ?? a.username,
        email: a.username,
      }
    );
  }
  return null;
}

export function activeProvider(): Provider | null {
  return active;
}

export async function getAccessToken(): Promise<string> {
  if (active === "google") return google.getAccessToken();
  return msal.getAccessToken();
}

export async function signOut(): Promise<void> {
  if (active === "google") {
    google.signOut();
    // No Google logout redirect: signing the user out of the PORTAL should not
    // sign them out of Google itself, which is where their mail and documents
    // are. Clearing the portal session and returning to the sign-in page is
    // the whole job.
    window.location.assign("/");
    return;
  }
  return msal.signOut();
}

/** Start an interactive Microsoft sign-in. */
export async function signIn(path: msal.TrustPath = "external"): Promise<void> {
  return msal.signIn(path);
}

/** Start an interactive Google sign-in. Leaves the page. */
export function signInWithGoogle(): void {
  google.startSignIn();
}

/** Why the last Google sign-in failed, if it did. Cleared once read. */
export function takeGoogleSignInError(): string | null {
  return google.takeSignInError();
}

export { PortalNetworkError } from "./msal";
export type { TrustPath } from "./msal";
